import { computed, signal } from "@angular/core";
import { ExplorableNavItem } from "@interfaces";

const EXPLORER_NAV_KEY = 'explorer-navigator-key';
const MAX_RECENTS_SIZE = 100;

/**
 * The state of the explorer history stack and the current active index.
 * Index 0 represents the newest item in the forward/back stack.
 */
interface ExplorerState {
	readonly history: ReadonlyArray<ExplorableNavItem>;
	readonly currentIndex: number;
}

/**
 * Manages the queue of navigations (current and prior) and the currently selected index.
 */
export class ExplorerNavigator {

	/**
	 * Initializes the queue by reading the deduplicated list from local storage 
	 * or by returning a hard-coded initial value.
	 */
	#loadState(): ExplorerState {
		try {
			const stored = localStorage.getItem(EXPLORER_NAV_KEY);
			if (stored) {
				const history = JSON.parse(stored);
				if (Array.isArray(history) && history.length) {
					// Safety check: strip tabs in case an older version saved them
					const cleanedHistory = history.map((item: ExplorableNavItem) => {
						const { tabIndex, ...rest } = item;
						return rest as ExplorableNavItem;
					});
					return { history: cleanedHistory, currentIndex: 0 };
				}
			}
		}
		catch { }

		return {
			history: [
				{ noun: 'Person', id: 1, name: 'Gary Chamberlain', hasRedFlag: false }
			],
			currentIndex: 0,
		};
	}

	/**
	 * The single source of truth for the navigation stack.
	 */
	readonly #state = signal<ExplorerState>(this.#loadState());
	readonly state = this.#state.asReadonly();

	/**
	 * The currently selected item in the queue.
	 */
	readonly currentItem = computed(() => {
		const state = this.#state();
		return state.history[state.currentIndex]!;
	});

	/**
	 * The active tab for the currently viewed item in history. If no tab has been set, defaults to 0 (main tab).
	 */
	readonly currentTab = computed(() => {
		return this.currentItem().tabIndex ?? 0;
	});

	/**
	 * Updates the active tab of the currently viewed history entry.
	 */
	setActiveTab(tabIndex: number) {
		const state = this.#state();
		const history = [...state.history];
		const current = history[state.currentIndex]!;

		history[state.currentIndex] = { ...current, tabIndex };
		this.#state.set({ ...state, history });
	}

	/**
	 * Whether there is at least one older item to back up to.
	 */
	readonly canGoBack = computed(() => {
		const state = this.#state();
		return state.currentIndex < state.history.length - 1;
	});

	/**
	 * Backup and show the prior item in the history stack.
	 */
	goBack() {
		if (!this.canGoBack()) return;
		this.#state.update(s => ({ ...s, currentIndex: s.currentIndex + 1 }));
		this.#saveToStorage();
	}

	/**
	 * Whether it is possible to return to a newer item after a call to goBack().
	 */
	readonly canGoForward = computed(() => {
		const state = this.#state();
		return state.currentIndex > 0;
	});

	/**
	 * Return to the next newer item after a call to goBack().
	 */
	goForward() {
		if (!this.canGoForward()) return;
		this.#state.update(s => ({ ...s, currentIndex: s.currentIndex - 1 }));
		this.#saveToStorage();
	}

	/**
	 * Go to a new item. Allows duplicate items in the exact live history sequence.
	 */
	go(item: ExplorableNavItem) {
		const state = this.#state();

		const activeHistory = state.currentIndex > 0
			? state.history.slice(state.currentIndex)
			: state.history;

		this.#state.set({
			history: [item, ...activeHistory],
			currentIndex: 0
		});

		this.#saveToStorage();
	}

	/**
	 * A dynamically computed, deduplicated list of recent items. 
	 * Tab tracking is explicitly stripped out here so it doesn't leak into storage.
	 */
	readonly recents = computed(() => {
		const { history, currentIndex } = this.#state();
		const seen = new Set<string>();
		const unique: ExplorableNavItem[] = [];

		const addIfUnique = (item: ExplorableNavItem) => {
			const key = `${item.noun}-${item.id}`;
			if (!seen.has(key)) {
				seen.add(key);

				// Destructure to isolate the tab, pushing only the remaining properties
				const { tabIndex, ...itemWithoutTab } = item;
				unique.push(itemWithoutTab as ExplorableNavItem);
			}
		};

		// Prioritize currently viewed item and older history
		for (let i = currentIndex; i < history.length; i++) {
			addIfUnique(history[i]!);
			if (unique.length === MAX_RECENTS_SIZE) return unique;
		}

		// Add any newer "forward" history to the bottom of the recents list
		for (let i = currentIndex - 1; i >= 0; i--) {
			addIfUnique(history[i]!);
			if (unique.length === MAX_RECENTS_SIZE) return unique;
		}

		return unique;
	});

	/**
	 * Persists the deduplicated 'recents' list (which lacks tab data) to web storage.
	 */
	#saveToStorage() {
		localStorage.setItem(EXPLORER_NAV_KEY, JSON.stringify(this.recents()));
	}

	/**
	 * Updates the displayed name/flags of a noun/id combination across all 
	 * instances within the live history.
	 */
	updateItem(updated: ExplorableNavItem) {
		const state = this.#state();
		let hasChanges = false;

		const updatedHistory = state.history.map(item => {
			if (item.noun === updated.noun && item.id === updated.id) {
				if (item.name !== updated.name || item.hasRedFlag !== updated.hasRedFlag) {
					hasChanges = true;
					// Note: spreading `item` first preserves its live `tab` state!
					return { ...item, name: updated.name, hasRedFlag: updated.hasRedFlag };
				}
			}
			return item;
		});

		if (hasChanges) {
			this.#state.set({ ...state, history: updatedHistory });
			this.#saveToStorage();
		}
	}
}